import React, { useState } from 'react';
import { Download, Mail, FileText } from 'lucide-react';
import FilterBar from './FilterBar';
import { getAuthHeaders, WORKER_URL } from '../../utils/api';

const toDateStr = (date) => date.toISOString().split('T')[0];

const generateCSV = (orders) => {
    const header = ['Data', 'Nº Pedido', 'Produto', 'Qtd', 'Valor Unit.', 'Valor Total', 'Comprador', 'Pagamento', 'Envio'];
    const STATUS_LABELS = { paid: 'Pago', pending: 'Pendente', cancelled: 'Cancelado' };
    const SHIPPING_LABELS = { pending: 'Aguardando', handling: 'Em manuseio', ready_to_ship: 'Pronto p/ envio', shipped: 'Enviado', delivered: 'Entregue', not_delivered: 'Não entregue', cancelled: 'Cancelado' };

    const fmt = (v) => `"${parseFloat(v || 0).toFixed(2).replace('.', ',')}"`;
    const fmtDate = (d) => {
        const dt = new Date(d);
        const day = String(dt.getDate()).padStart(2, '0');
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const year = String(dt.getFullYear()).slice(-2);
        const hour = String(dt.getHours()).padStart(2, '0');
        const min = String(dt.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hour}:${min}`;
    };

    const rows = orders.map(o => [
        fmtDate(o.date_created),
        o.id,
        `"${(o.title || '').replace(/"/g, '""')}"`,
        o.quantity,
        fmt(o.unit_price),
        fmt(o.total_amount),
        o.buyer,
        STATUS_LABELS[o.status] || o.status,
        SHIPPING_LABELS[o.shipping_status] || '—'
    ]);

    return [header, ...rows].map(r => r.join(';')).join('\n');
};

const Documents = () => {
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    orderNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportLoading, setReportLoading] = useState(false);

  // Função para mostrar mensagens temporárias
  const showMessage = (text, isError = false) => {
    setMessage({ text, isError });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);

      if (filters.orderNumber) {
        const response = await fetch(`${WORKER_URL}/api/invoices/single`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: filters.orderNumber
          })
        });

        if (!response.ok) throw new Error('Erro ao buscar nota fiscal');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `nota-fiscal-${filters.orderNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);

        showMessage('PDF baixado com sucesso!');
      } else if (filters.startDate && filters.endDate) {
        const formatDate = (date) => date.toISOString().split('T')[0].replace(/-/g, '');

        const response = await fetch(`${WORKER_URL}/api/invoices/bulk`, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start: formatDate(filters.startDate),
            end: formatDate(filters.endDate)
          })
        });

        if (!response.ok) throw new Error('Erro ao buscar notas fiscais');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notas-fiscais-${formatDate(filters.startDate)}-${formatDate(filters.endDate)}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);

        showMessage('XMLs baixados com sucesso!');
      }

      // Limpa os filtros após o download
      setFilters({
        startDate: null,
        endDate: null,
        orderNumber: ''
      });
    } catch (error) {
      console.error('Erro:', error);
      // Se for erro 429, mostra mensagem específica
      const errorMessage = error.response?.status === 429
        ? 'Muitas requisições. Por favor, aguarde um momento e tente novamente.'
        : 'Erro ao baixar documentos';

      showMessage(errorMessage, true);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!reportFrom || !reportTo) return showMessage('Selecione o período', true);
    setReportLoading(true);
    try {
      const params = new URLSearchParams({ dateFrom: reportFrom, dateTo: reportTo, status: 'all' });
      const res = await fetch(`${WORKER_URL}/api/orders/history?${params}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!data.orders?.length) return showMessage('Nenhuma venda no período selecionado', true);

      const csv = generateCSV(data.orders);
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-vendas-${reportFrom}-${reportTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showMessage(`Relatório exportado — ${data.orders.length} pedidos`);
    } catch (e) {
      showMessage('Erro ao gerar relatório', true);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#2D3277', marginBottom: '20px' }}>
        Documentos Fiscais
      </h2>

      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        onDownload={handleDownload}
        loading={loading}
      />

      {/* Relatório de Vendas */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginTop: '20px' }}>
        <h3 style={{ color: '#2D3277', marginBottom: '16px', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileText size={18} /> Relatório de Vendas
        </h3>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Data Inicial</label>
            <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)}
              max={reportTo || undefined}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Data Final</label>
            <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)}
              min={reportFrom || undefined} max={new Date().toISOString().split('T')[0]}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }} />
          </div>
          <button
            onClick={handleExportCSV}
            disabled={reportLoading || !reportFrom || !reportTo}
            style={{ padding: '8px 18px', backgroundColor: '#2D3277', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: reportLoading ? 0.7 : 1 }}>
            <Download size={16} />
            {reportLoading ? 'Gerando...' : 'Exportar CSV'}
          </button>
        </div>
        <p style={{ color: '#999', fontSize: '0.8rem', marginTop: '10px' }}>
          Exporta todos os pedidos do período em formato CSV para contabilidade.
        </p>
      </div>

      {/* Mensagem de feedback */}
      {message && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: message.isError ? '#ff4444' : '#00a650',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          animation: 'fadeIn 0.3s ease'
        }}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default Documents;